import React, { useRef, useEffect, useState } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Chip,
    Box,
    IconButton,
    Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { userRequest } from '../../requestMethods';

const StyledCard = styled(Card)(({ theme }) => ({
    minWidth: 300,
    maxWidth: 400,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    borderRadius: 12,
    '& .MuiCardContent-root': {
        padding: theme.spacing(3),
    },
}));

const WordText = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '1.2rem',
    display: 'inline',
}));

const PosText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '1rem',
    display: 'inline',
    marginLeft: theme.spacing(1),
}));

const TranslationText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '1.2rem',
    fontWeight: 500,
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const WordCard = ({ word, onClose, source = null, onWordDisplayed = null }) => {
    const [isLiked, setIsLiked] = useState(false);
    const [loading, setLoading] = useState(false);
    const closeButtonRef = useRef(null);

    useEffect(() => {
        // Focus the close button when the card opens
        if (closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
        
        // Use optimized like status from API if available, otherwise check manually
        if (word.isLiked !== undefined) {
            setIsLiked(word.isLiked);
        } else if (word._id) {
            checkLikeStatus();
        }

        // Notify parent component that a word is being displayed
        if (onWordDisplayed && word) {
            onWordDisplayed(word);
        }
    }, [word._id, word.isLiked, word, onWordDisplayed]);

    const checkLikeStatus = async () => {
        if (!word._id) return;
        
        try {
            const response = await userRequest.get(`/user/likes/check/Word/${word._id}`);
            setIsLiked(response.data.isLiked);
        } catch (error) {
            console.error('Error checking like status:', error);
        }
    };

    const handlePlayAudio = () => {
        const utterance = new SpeechSynthesisUtterance(word.w);
        utterance.rate = 0.85; // Slow down the speech rate (1.0 is normal speed, lower is slower)
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
    };

    const handleToggleLike = async () => {
        if (!word._id || loading) return;
        
        setLoading(true);
        try {
            const requestBody = {
                contentType: 'Word',
                contentId: word._id
            };
            
            // Add source context if available (e.g., from which story the word was liked)
            if (source) {
                requestBody.source = source;
            }
            
            const response = await userRequest.post('/user/likes/toggle', requestBody);
            console.log(response)
            setIsLiked(response.data.isLiked);
        } catch (error) {
            console.error('Error toggling like:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    };

    return (
        <StyledCard onKeyDown={handleKeyDown} role="dialog" aria-label="Word details">
            <CardContent>
                {/* First line: Word, POS, Phrasal Verb indicator, and Speaker icon */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <WordText variant="h6">
                            {word.w}
                        </WordText>
                        {word.p && (   // POS
                            <PosText variant="body1">
                                {word.p}
                            </PosText>
                        )}
                        {word.pv && ( // word.isPhrasalVerb
                            <PosText variant="body1">
                                phrase
                            </PosText>
                        )}
                    </Box>
                    <IconButton 
                        onClick={handlePlayAudio} 
                        size="small"
                        aria-label="Play pronunciation"
                    >
                        <VolumeUpIcon />
                    </IconButton>
                </Box>

                {/* Horizontal line */}
                <Divider sx={{ my: 2 }} />

                {/* Translation - centered and darker */}
                {word.t ? (
                    <TranslationText variant="h6">
                        {word.t}
                    </TranslationText>
                ) : (
                    <TranslationText 
                        variant="body1" 
                        sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.9rem'
                        }}
                    >
                        tarjima qilinmaydi
                    </TranslationText>
                )}

                {/* Horizontal line only if grammar link exists */}
                {word.l && word.lt && (  // word.link and word.linkTitle
                    <Divider sx={{ my: 2 }} />
                )}

                {/* Grammar link */}
                {word.l && word.lt && (  // word.link and word.linkTitle
                    <Box textAlign="center">
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Grammar:
                        </Typography>
                        <Typography
                            component="a"
                            href={word.l} // word.link
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {word.lt}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </StyledCard>
    );
};

export default WordCard;